import { normalizePartKind, type HomePartKind } from "@/home/part-kind";

export const HOME_ZONES = ["floor", "wall", "sky"] as const;
export type HomeZone = (typeof HOME_ZONES)[number];

export function isHomeZone(value: unknown): value is HomeZone {
  return typeof value === "string" && (HOME_ZONES as readonly string[]).includes(value);
}

export function normalizeZones(value: unknown, kind: HomePartKind): HomeZone[] {
  if (Array.isArray(value)) {
    const zones = value.filter(isHomeZone);
    if (zones.length > 0) return [...new Set(zones)];
  }
  return defaultZonesForKind(kind);
}

export function defaultZonesForKind(kind: HomePartKind): HomeZone[] {
  switch (normalizePartKind(kind)) {
    case "backdrop":
    case "shell":
      return ["floor", "wall", "sky"];
    case "floor":
      return ["floor"];
    case "platform":
      return ["floor", "wall"];
    case "solid":
      return ["floor", "wall"];
    case "prop":
    case "attack_dummy":
    case "monster":
      return ["floor", "wall"];
    default:
      return ["floor"];
  }
}

/** Row bands on the room grid (top → bottom on screen). */
export function zoneAtRow(row: number, gridRows: number): HomeZone {
  if (gridRows <= 2) return "floor";
  const skyEnd = Math.max(1, Math.floor(gridRows * 0.25));
  const wallEnd = Math.max(skyEnd + 1, Math.floor(gridRows * 0.5));
  if (row < skyEnd) return "sky";
  if (row < wallEnd) return "wall";
  return "floor";
}

export function rectAllowedInZones(
  gridY: number,
  footprintH: number,
  gridRows: number,
  allowed: HomeZone[],
): boolean {
  if (allowed.length === 0) return true;
  if (footprintH <= 0) return true;
  const feetRow = Math.min(gridY + footprintH - 1, gridRows - 1);
  return allowed.includes(zoneAtRow(feetRow, gridRows));
}

export function zoneSelectOptionsHtml(selected: HomeZone[] = ["floor"]): string {
  return HOME_ZONES.map((zone) => {
    const labels: Record<HomeZone, string> = {
      floor: "พื้น",
      wall: "ผนัง",
      sky: "ฟ้า",
    };
    const sel = selected.includes(zone) ? " selected" : "";
    return `<option value="${zone}"${sel}>${labels[zone]}</option>`;
  }).join("");
}
